import { Dispatch, MouseEvent, SetStateAction, useCallback, useEffect, useState } from 'react'
import { FieldInputProps, FieldMetaState } from 'react-final-form'
import { FILE_SELECTED_INITIAL_STATE } from 'components/FileUploader/constants'
import { FileRejection } from 'react-dropzone'
import { IFileSelected } from 'components/FileUploader/interface'

type UseFileUploaderType = {
  errorMessage: string
  fileSelected: IFileSelected
  handleClearSelection: (e: MouseEvent<HTMLElement>) => void
  isFileAccepted: boolean
  isFileRejected: boolean
  onDrop: (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>) => void
  setFileSelected: Dispatch<SetStateAction<IFileSelected>>
}

export function useFileUploader(
  meta: FieldMetaState<File | undefined>,
  input: FieldInputProps<File | undefined, HTMLElement>,
  onChange?: (acceptedFiles: File[], fileRejections: FileRejection[]) => void
): UseFileUploaderType {
  const [fileSelected, setFileSelected] = useState<IFileSelected>(FILE_SELECTED_INITIAL_STATE)
  const [isFileAccepted, setIsFileAccepted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const { file } = fileSelected
    const existFile = file !== undefined
    if (existFile) input.onChange(file)
    const isAccept: boolean = !fileSelected.rejected && existFile
    setIsFileAccepted(isAccept)
  }, [fileSelected])

  useEffect(() => {
    if (meta?.submitFailed && meta.submitError) {
      setFileSelected({
        file: undefined,
        rejected: true
      })
      setErrorMessage(meta?.submitError || meta?.error)
    }
  }, [meta.submitFailed, meta.submitError])

  useEffect(() => {
    if (meta.modified && meta?.error) {
      setErrorMessage(meta?.error)
    }
  }, [meta.modified, meta.error])

  const onDrop = useCallback((acceptedFiles: Array<File>, fileRejections: Array<FileRejection>): void => {
    if (acceptedFiles.length > 0) setErrorMessage('')
    const rejectedFile: IFileSelected = { file: fileRejections?.at(0)?.file, rejected: true }
    const acceptedFile: IFileSelected = { file: acceptedFiles?.at(0), rejected: false }
    setFileSelected(acceptedFiles.length > 0 ? acceptedFile : rejectedFile)
    onChange && onChange(acceptedFiles, fileRejections)
  }, [])

  const handleClearSelection = (e: MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setFileSelected(FILE_SELECTED_INITIAL_STATE)
    input.onChange('')
  }

  return {
    fileSelected,
    errorMessage,
    handleClearSelection,
    isFileAccepted,
    isFileRejected: fileSelected.rejected,
    onDrop,
    setFileSelected
  }
}
